import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { Props } from "../api/types";

const MIN_DATE = dayjs("1900-01-01");
const MAX_DATE = dayjs("2100-12-31");

const CustomDateRangeDialog = ({ open, onClose, onApply }: Props) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const [startError, setStartError] = useState<string | null>(null);
  const [endError, setEndError] = useState<string | null>(null);

  const isValidDate = (d: Dayjs | null) =>
    !!d && d.isValid() && d.isAfter(MIN_DATE) && d.isBefore(MAX_DATE);

  useEffect(() => {
    if (!open) {
      setStartError(null);
      setEndError(null);
    }
  }, [open]);

  const handleApply = () => {
    if (!isValidDate(startDate)) {
      setStartError("Invalid start date");
      return;
    }

    if (!isValidDate(endDate)) {
      setEndError("Invalid end date");
      return;
    }

    if (startDate!.isAfter(endDate!)) {
      setEndError("End date must be after start date");
      return;
    }

    onApply({
      startDate: startDate!.startOf("day").toDate(),
      endDate: endDate!.endOf("day").toDate(),
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderRadius: 4,
            width: 520,
          },
        }}
        BackdropProps={{
          sx: { backgroundColor: "rgba(15,23,42,0.35)" },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            color: "primary.main",
            fontSize: 20,
          }}
        >
          Select Date Range
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          <Stack direction="row" spacing={2} mt={2}>
            <DatePicker
              label="Start Date"
              value={startDate}
              minDate={MIN_DATE}
              maxDate={MAX_DATE}
              onChange={(date) => {
                setStartDate(date);

                if (!isValidDate(date)) {
                  setStartError("Invalid start date");
                } else {
                  setStartError(null);
                  setEndError(null);
                }
              }}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                  error: Boolean(startError),
                  helperText: startError,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  },
                },
              }}
            />

            <DatePicker
              label="End Date"
              value={endDate}
              minDate={startDate ?? MIN_DATE}
              maxDate={MAX_DATE}
              onChange={(date) => {
                setEndDate(date);

                if (!isValidDate(date)) {
                  setEndError("Invalid end date");
                } else {
                  setEndError(null);
                }
              }}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                  error: Boolean(endError),
                  helperText: endError,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  },
                },
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={onClose}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              textTransform: "none",
              borderRadius: 10,
              px: 3,
            }}
          >
            Select
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CustomDateRangeDialog;
